import React, { useState } from "react";
import { Briefcase, Users, Plane, CheckCircle } from "lucide-react";
import AnimatedSection from "@/components/animation/AnimatedSection/AnimatedSection";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import OptImg from "@/components/OptImg";

// HERO

const HeroSection = () => {
  const { t } = useTranslation("about");
  return (
    <section className="min-h-screen bg-white flex flex-col">
      <div className="w-full pt-8 px-6 sm:px-12 flex flex-col sm:flex-row justify-between items-start">
        <div className="flex flex-col sm:flex-row sm:items-start">
          <div className="flex flex-row items-start">
            <span className="text-[70px] sm:text-[150px] leading-none font-bold text-black font-sans tracking-tight select-none">
              {t("hero.titleLine1")} <br /> {t("hero.titleLine2")}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end pr-6 md:pr-24 mt-4">
        <div className="max-w-xl text-right">
          <h2 className="text-[32px] md:text-[48px] font-bold text-[#101616] mb-2">
            {t("hero.aboutTitle")}
          </h2>
          <p className="text-[18px] md:text-[22px] text-[#101616] font-normal leading-snug">
            {t("hero.aboutDesc")}
          </p>
        </div>
      </div>
    </section>
  );
};

// TEAM
const clampLines = 6;

const TeamCard = ({ member, reverse }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`flex flex-col md:flex-row ${
        reverse ? "md:flex-row-reverse" : ""
      } bg-transparent rounded-3xl overflow-hidden min-h-[220px]`}
    >
      <OptImg
        src={member.img}
        alt={member.name}
        className={`w-full h-[220px] md:w-[350px] md:h-auto object-cover object-center transition duration-500 ${
          reverse
            ? "md:rounded-l-none md:rounded-r-3xl"
            : "md:rounded-r-none md:rounded-l-3xl"
        } rounded-t-3xl md:rounded-t-none`}
        loading="lazy"
        draggable={false}
      />
      <div className="flex flex-col gap-2 p-6 flex-1">
        <div className="text-2xl font-bold text-[#101616] mb-2">
          {member.name}
        </div>
        {member.subtext && (
          <div className="text-sm text-gray-500 mb-2">{member.subtext}</div>
        )}
        <div
          className={`text-[17px] text-[#101616] text-left leading-relaxed ${
            !expanded ? "line-clamp-6 md:line-clamp-none" : ""
          } transition-all`}
        >
          {member.desc}
        </div>
        {member.desc.split("\n").length > clampLines && (
          <button
            className="mt-2 self-end text-sm text-[#1746A2] font-semibold hover:underline transition block md:hidden"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>
    </div>
  );
};

const BehindTitanSection = () => {
  const { t } = useTranslation("about");
  const teamData = t("team", { returnObjects: true }).map((member) => ({
    ...member,
    img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_TITAN_CONTAINRS_vvwv0z.jpg",
  }));

  return (
    // Changed bg-white to a light gray-blue for separation
    <section className={`bg-white py-16 px-4 md:px-12 `} id='overview'>
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 max-w-3xl">
          <h1 className="text-4xl md:text-[56px] font-bold text-[#101616] mb-4">
            {t("behindTitan.title")}
          </h1>
          <p className="text-lg md:text-2xl text-[#101616] font-normal leading-snug">
            {t("behindTitan.desc")}
          </p>
          {/* Smaller text under desc */}
          <p className="text-sm md:text-base text-[#555] mt-3">
            {t("behindTitan.subDesc")}
          </p>
        </div>
        <div className="flex flex-col gap-10">
          <TeamCard key={teamData[0].name} member={teamData[0]} />
        </div>
      </div>
    </section>
  );
};

const BehindTitanSection2 = () => {
  const { t } = useTranslation("about");
  const teamData = t("team", { returnObjects: true }).map((member) => ({
    ...member,
    img: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237553/Bagian_DS_SOLUTIONS_h3dmux.jpg",
  }));

  return (
    <section className="bg-gradient-to-b from-[#f8fafc] to-white py-16 px-4 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 max-w-3xl">
<h2 className="text-4xl md:text-[56px] font-bold text-[#101616] mb-4 md:whitespace-nowrap">

            {t("behindTitan.title2")}
          </h2>
          <p className="text-lg md:text-2xl text-[#101616] font-normal leading-snug">
            {t("behindTitan.desc2")}
          </p>
          <p className="text-sm md:text-base text-[#555] mt-3">
            {t("behindTitan.subDesc")}
          </p>
        </div>
        <div className="flex flex-col gap-10">
          <TeamCard key={teamData[1].name} member={teamData[1]} />
        </div>
      </div>
    </section>
  );
};

// WHY TITAN
const WhyTitanSection = () => {
  const { t } = useTranslation("about");
  const items = t("whyTitan.items", { returnObjects: true });
  const icons = [<Briefcase />, <Users />, <Plane />, <CheckCircle />];

  return (
    <section className="bg-white py-20 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-[56px] font-bold text-[#101616] mb-16">
          {t("whyTitan.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-20">
          {items.map((item, idx) => (
            <div key={item.title} className="flex items-start">
              <div className="min-w-[104px] min-h-[104px] flex items-center justify-center border-2 border-dashed border-[#101616] rounded-br-[32px] mr-8 mt-2">
                <div
                  className="text-[#101616] opacity-90"
                  style={{ fontSize: 48 }}
                >
                  {icons[idx]}
                </div>
              </div>
              <div>
                <div className="text-[28px] font-bold text-[#101616] mb-2">
                  {item.title}
                </div>
                <div className="text-[20px] text-[#101616] font-normal text-left leading-snug">
                  {item.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const OurClientsSection = ({ clients = [] }) => {
  const { t } = useTranslation("about");

  // split into 3 roughly equal rows
  const chunkSize = Math.ceil(clients.length / 3);
  const row1 = clients.slice(0, chunkSize);
  const row2 = clients.slice(chunkSize, chunkSize * 2);
  const row3 = clients.slice(chunkSize * 2);

  return (
    <section
      className="bg-gradient-to-b from-[#f8fafc] to-white py-20 px-6 md:px-16 border-t border-[#101616]/10"
      id="clients"
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-[56px] font-bold text-[#101616] mb-16 text-center md:text-left">
          {t("clients.title")}
        </h2>

        {/* MOBILE */}
        <div className="grid grid-cols-3 gap-x-6 gap-y-8 md:hidden">
          {clients.map((client, idx) => (
            <div
              key={idx}
              className="flex items-center justify-center h-[64px]"
            >
              <OptImg
                src={client.image}
                maxWidth={400}
                alt={client.name}
                className="object-contain max-h-10 w-auto transition duration-200"
                loading="lazy"
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* DESKTOP */}
        <div className="hidden md:flex flex-col gap-y-10">
          <div className="flex flex-wrap justify-center gap-x-16 gap-y-8">
            {row1.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center h-[90px]"
              >
                <OptImg
                  src={client.image}
                maxWidth={400}
                  alt={client.name}
                  className="object-contain max-h-20 w-auto transition duration-200"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-x-16 gap-y-8">
            {row2.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center h-[90px]"
              >
                <OptImg
                  src={client.image}
                maxWidth={400}
                  alt={client.name}
                  className="object-contain max-h-20 w-auto transition duration-200"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-x-16 gap-y-8">
            {row3.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center h-[90px]"
              >
                <OptImg
                  src={client.image}
                maxWidth={400}
                  alt={client.name}
                  className="object-contain max-h-20 w-auto transition duration-200"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const OurCertificationSection = ({ certifications = [] }) => {
  const midpoint = Math.ceil(certifications.length / 2);
  const row1 = certifications.slice(0, midpoint);
  const row2 = certifications.slice(midpoint);
  const { t } = useTranslation("about");

  return (
    <section className="bg-gradient-to-b from-[#f8fafc] to-white py-20 px-6 md:px-16 border-t border-[#101616]/10" id='certifications'>

      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-[56px] font-bold text-[#101616] mb-16 text-center md:text-left">
          {t("certifications.title")}
        </h2>

        {/* MOBILE */}
        <div className="grid grid-cols-3 gap-x-6 gap-y-8 md:hidden">
          {certifications.map((client, idx) => (
            <div
              key={idx}
              className="flex items-center justify-center h-[64px]"
            >
              <OptImg
                src={client.image}
                maxWidth={400}
                alt={client.name}
                className="object-contain max-h-10 w-auto transition duration-200"
                loading="lazy"
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* DESKTOP */}
        <div className="hidden md:flex flex-col gap-y-10">
          <div className="flex flex-wrap justify-center gap-x-16 gap-y-8">
            {row1.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center h-[90px]"
              >
                <OptImg
                  src={client.image}
                maxWidth={400}
                  alt={client.name}
                  className="object-contain max-h-20 w-auto transition duration-200"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-16 gap-y-8">
            {row2.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center h-[90px]"
              >
                <OptImg
                  src={client.image}
                maxWidth={400}
                  alt={client.name}
                  className="object-contain max-h-20 w-auto transition duration-200"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ABOUT PAGE
const AboutPage = () => {
  const clients = [
    {
      name: "a",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216624/image_40_mrz1pa.png",
    },
    {
      name: "b",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216624/image_32_n37xfy.png",
    },
    {
      name: "Amazon",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216624/image_33_aiii0i.png",
    },
    {
      name: "Netflix",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216630/image_34_zbwvuu.png",
    },
    {
      name: "Microsoft",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216627/image_35_ypjz0i.png",
    },
    {
      name: "Spotify",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216624/image_36_qp6m4r.png",
    },
    {
      name: "Slack",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216627/image_39_a4paqg.png",
    },
    {
      name: "Airbnb",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216627/image_38_xbvyih.png",
    },
    {
      name: "Stripe",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754216625/image_37_wl5ul9.png",
    },
    {
      name: "Hokben",
      image:
        "https://res.cloudinary.com/dt1jmjt25/image/upload/v1759153078/Vector_qrbobn.png",
    },
    {
      name: "NinExpress",
      image:
        "https://res.cloudinary.com/dt1jmjt25/image/upload/v1759152990/pngegg_c1aioq.png",
    },
    {
      name: "podluck",
      image:
        "https://res.cloudinary.com/dt1jmjt25/image/upload/v1759153084/podlucks_uago8a.png",
    },
    {
      name: "aice",
      image:
        "https://res.cloudinary.com/dt1jmjt25/image/upload/v1759153088/aice_vl8qym.png",
    },
  ];

  const certifications = [
    {
      name: "a",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202143/image_21_q1uzv5.png",
    },
    {
      name: "b",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202145/image_23_i16byu.png",
    },
    {
      name: "c",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202143/image_24_sizftf.png",
    },
    {
      name: "d",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202145/image_25_ssdml5.png",
    },
    {
      name: "f",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202156/image_27_lmprvw.png",
    },
    {
      name: "g",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202142/image_28_rgq3rp.png",
    },
    {
      name: "h",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202146/image_31_g8q7bt.png",
    },
    {
      name: "i",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202144/image_30_i6un4v.png",
    },
    {
      name: "j",
      image:
        "https://res.cloudinary.com/dmvui9sur/image/upload/v1754202148/image_29_kbxtq1.png",
    },
  ];


  function ScrollToHash() {
    const { hash } = useLocation();

    useEffect(() => {
      if (!hash) return;
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [hash]);

    return null;
  }
  return (
    <>
      <ScrollToHash />
      <AnimatedSection>
        <BehindTitanSection />
      </AnimatedSection>
      <AnimatedSection>
        <BehindTitanSection2 />
      </AnimatedSection>
      {/* <AnimatedSection>
        <WhyTitanSection />
      </AnimatedSection> */}
      <AnimatedSection>
        <OurCertificationSection certifications={certifications} />
      </AnimatedSection>
      <AnimatedSection>
        <OurClientsSection clients={clients} />
      </AnimatedSection>
    </>
  );
};

export default AboutPage;
